import { Avatar, Box, HStack, IconButton, Image, Stack, Text, Menu, MenuButton, MenuList, MenuItem, Button, useDisclosure, Icon, VStack, Tooltip, useToast } from "@chakra-ui/react";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { colors } from "../../../styles";
import { formatRelativeTime, typeToKor } from "../../../utils";
import MyIcon from "../../Icon/MyIcon";
import { deleteChat, toggleBlockProfile, toggleLikeChat, toggleReportChat, toggleSaveChat } from "../../../api";
import { InfiniteData, useMutation, useQueryClient } from "@tanstack/react-query";
import { IChatList, IUserType } from "../../../types";
import ConfirmAlert, { useConfirmAlert } from "../ConfirmAlert";
import { useChatDeleteLoading } from "../ChatDeleteLoading";
import AnimatedReactionIcon from "../AnimatedReactionIcon";
import ImageModal from "../../Modals/ImageModal";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import TooltipIcon from "../../Icon/TooltipIcon";
import LikesModal from "../../../routes/Chat/LikesModal";

interface ChatListItemProps {
    id: string;
    slug: string;
    profileId: string;
    profileImageUrl?: string;
    userType: IUserType;
    authorType: IUserType;
    nickname: string;
    content: string;
    imageUrls?: string[];
    isMyTypeOnly: boolean;
    likeCount: number;
    commentCount: number;
    saveCount: number;
    isMine: boolean;
    isLiked: boolean;
    isSaved: boolean;
    isReported: boolean;
    isBlocking: boolean;
    hiddenAt: Date | null;
    createdAt: Date;
    onDeleteSuccess?: () => void;
    isFirst: boolean;
    isLast: boolean;
}

function ChatListItem({
    id,
    slug,
    profileId,
    profileImageUrl,
    nickname,
    userType,
    authorType,
    content,
    imageUrls = [],
    isMyTypeOnly,
    likeCount,
    commentCount,
    saveCount,
    isMine,
    isLiked,
    isSaved,
    isReported,
    isBlocking,
    hiddenAt,
    createdAt,
    onDeleteSuccess,
    isFirst,
    isLast,
}: ChatListItemProps) {

    const isTypeChanged = useMemo(() => {
        return authorType !== userType;
    }, [authorType, userType]);

    /* queryClient */
    
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { showConfirm, confirmAlertProps } = useConfirmAlert();
    const deleteLoading = useChatDeleteLoading();
    const toast = useToast();
    const [likePulse, setLikePulse] = useState(0);
    const { isOpen: isLikesModalOpen, onOpen: onLikesModalOpen, onClose: onLikesModalClose } = useDisclosure();
    const [savePulse, setSavePulse] = useState(0);

    const handleGoToProfile = () => {
        navigate(`/chat/profile/${nickname}`);
    };

    const handleGoToChatDetail = () => {
        navigate(`/chat/${slug}`);
    };

    /* toggleLikeChatMutation */

    const toggleLikeChatMutation = useMutation({
        mutationFn: toggleLikeChat,
        onMutate: async () => {
            await queryClient.cancelQueries({ queryKey: ["chats"] });

            const previousQueries = queryClient.getQueriesData<InfiniteData<IChatList>>({
                queryKey: ["chats"],
            });

            queryClient.setQueriesData<InfiniteData<IChatList>>(
                { queryKey: ["chats"] },
                (old) => {
                    if (!old) return old;
                    return {
                        ...old,
                        pages: old.pages.map((page) => ({
                            ...page,
                            chats: page.chats.map((chat) =>
                                chat.id === id
                                    ? {
                                        ...chat,
                                        isLiked: !chat.isLiked,
                                        likeCount: chat.likeCount + (chat.isLiked ? -1 : 1),
                                    }
                                    : chat
                            ),
                        })),
                    };
                },
            );

            return { previousQueries };
        },
        onError: (error, _vars, context) => {
            context?.previousQueries.forEach(([queryKey, data]) => {
                queryClient.setQueryData(queryKey, data);
            });
            console.error(error);
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ["chat", slug] });
        },
    });

    /* toggleSaveChatMutation */

    const toggleSaveChatMutation = useMutation({
        mutationFn: toggleSaveChat,
        onMutate: async () => {
            await queryClient.cancelQueries({ queryKey: ["chats"] });

            const previousQueries = queryClient.getQueriesData<InfiniteData<IChatList>>({
                queryKey: ["chats"],
            });

            queryClient.setQueriesData<InfiniteData<IChatList>>(
                { queryKey: ["chats"] },
                (old) => {
                    if (!old) return old;
                    return {
                        ...old,
                        pages: old.pages.map((page) => ({
                            ...page,
                            chats: page.chats.map((chat) =>
                                chat.id === id
                                    ? {
                                        ...chat,
                                        isSaved: !chat.isSaved,
                                        saveCount: chat.saveCount + (chat.isSaved ? -1 : 1),
                                    }
                                    : chat
                            ),
                        })),
                    };
                },
            );

            return { previousQueries };
        },
        onError: (error, _vars, context) => {
            context?.previousQueries.forEach(([queryKey, data]) => {
                queryClient.setQueryData(queryKey, data);
            });
            console.error(error);
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ["chat", slug] });
        },
    });

    /* toggleReportChatMutation */

    const toggleReportChatMutation = useMutation({
        mutationFn: toggleReportChat,
        onMutate: async () => {
            await queryClient.cancelQueries({ queryKey: ["chats"] });

            const previousQueries = queryClient.getQueriesData<InfiniteData<IChatList>>({
                queryKey: ["chats"],
            });

            queryClient.setQueriesData<InfiniteData<IChatList>>(
                { queryKey: ["chats"] },
                (old) => {
                    if (!old) return old;
                    return {
                        ...old,
                        pages: old.pages.map((page) => ({
                            ...page,
                            chats: page.chats.map((chat) =>
                                chat.id === id
                                    ? {
                                        ...chat,
                                        isReported: !chat.isReported,
                                    }
                                    : chat
                            ),
                        })),
                    };
                },
            );

            return { previousQueries };
        },
        onError: (error, _vars, context) => {
            context?.previousQueries.forEach(([queryKey, data]) => {
                queryClient.setQueryData(queryKey, data);
            });
            console.error(error);
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ["chats"] });
            queryClient.invalidateQueries({ queryKey: ["chat", slug] });
        },
    });

    /* deleteChatMutation */

    const deleteChatMutation = useMutation({
        mutationFn: deleteChat,
        onSuccess: () => {
            confirmAlertProps.onClose();
            deleteLoading?.done(() => onDeleteSuccess?.());
        },
        onError: (error) => {
            console.error(error);
            deleteLoading?.error();
            toast({
                title: "글 삭제에 실패했습니다.",
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    /* toggleBlockProfileMutation */

    const toggleBlockProfileMutation = useMutation({
        mutationFn: toggleBlockProfile,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["chats"] });
            queryClient.invalidateQueries({ queryKey: ["chat", slug] });
        },
        onError: (error) => {
            console.error(error);
        },
    });

    /* useDisclosure */

    const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);
    const { isOpen: isImageModalOpen, onOpen: onImageModalOpen, onClose: onImageModalClose } = useDisclosure();

    const openImage = (url: string) => {
        setSelectedImageUrl(url);
        onImageModalOpen();
    };

    const closeImage = () => {
        setSelectedImageUrl(null);
        onImageModalClose();
    };

    /* render */

    return (
        <Stack 
            width={{
                base:"100%",
                sm:"100%",
                md:"100%",
                lg:"80%",
                xl:"80%",
            }} 
            gap={3} 

            border={`0.5px solid ${colors.g4}`}

            // borderTopRadius={
            //     isFirst ? "30px" 
            //         : "0"
            // }
            // borderBottomRadius={
            //     isLast ? "30px" 
            //         : "0"
            // }

            borderRadius={"30px"}

            // borderTop={isFirst ? `0.5px solid ${colors.g4}` : `none`}
            // borderBottom={isLast ? `0.5px solid ${colors.g4}` : `none`}

            // borderLeft={'none'}
            // borderRight={'none'}
            
            padding={5}

            boxShadow={"0px 1px 2px 0px rgba(0, 0, 0, 0.1)"}
            // backgroundColor={'rgba(255, 255, 255, 0.2)'}
        >
            <HStack width={"100%"} justifyContent={"space-between"} alignItems={"flex-start"}>
                <Box
                    onClick={handleGoToProfile}
                    cursor={"pointer"}
                    flex={1}
                    minWidth={0}
                >
                    <HStack gap={2} alignItems={"center"}>
                        {profileImageUrl ?
                            <Image
                                src={profileImageUrl}
                                alt={`${nickname} 프로필 이미지`}
                                width={8}
                                height={8}
                                borderRadius={"full"}
                                objectFit={"cover"}
                                flexShrink={0}
                            />
                            :
                            <Image 
                                src={"/png/profile.png"}
                                alt="프로필 이미지"
                                width={8}
                                height={8}
                                borderRadius="full"
                                objectFit="cover"
                                flexShrink={0}
                            />
                        }
                        <VStack
                            justifyContent={"center"}
                            alignItems={"flex-start"}
                            spacing={0}
                        >
                            <Text fontSize={16} fontWeight={700} color={colors.g1}>
                                {nickname}
                            </Text>
                            <HStack
                                justifyContent={"flex-start"}
                                alignItems={"center"}
                                gap={1}
                            >
                                <Text fontSize={12} fontWeight={500} color={colors.g3}>
                                    {typeToKor(authorType)}
                                </Text>
                                {isTypeChanged && (
                                    <Tooltip label="작성 시점과 유형이 변경된 글입니다.">
                                        <TooltipIcon src={"/svg/info-link.svg"} alt="info" width={12} height={12} />
                                    </Tooltip>
                                )}
                            </HStack>
                        </VStack>
                    </HStack>
                </Box>

                {isMine ? 
                    <Button
                        variant={"ghost"}
                        color={colors.g2}
                        flexShrink={0}
                        onClick={(e) => {
                            e.stopPropagation(); // 클릭 이벤트 전파 방지
                            showConfirm({
                                message: "이 글을 삭제하시겠습니까?",
                                confirmLabel: "삭제",
                                closeOnConfirm: false,
                                onConfirm: () => {
                                    deleteLoading?.start();
                                    deleteChatMutation.mutate({ slug });
                                },
                            });
                        }}
                        disabled={deleteChatMutation.isPending}
                    >
                        <MyIcon src={"/svg/delete-g1.svg"} alt={"삭제"} width={16} height={16} />
                    </Button>
                :
                <Menu>
                    <MenuButton
                        as={IconButton}
                        aria-label='Options'
                        icon={<MyIcon src={"/svg/more-g1.svg"} alt={"더보기"} width={16} height={16} />}
                        variant='ghost'
                    />
                    <MenuList>
                        <MenuItem
                            onClick={() =>
                                showConfirm({
                                    message: isReported
                                        ? "신고를 취소하겠습니까?"
                                        : "이 글을 신고하겠습니까?",
                                    confirmLabel: isReported ? "신고 취소" : "신고",
                                    onConfirm: () => toggleReportChatMutation.mutate({ slug }),
                                })
                            }
                            disabled={toggleReportChatMutation.isPending}
                        >
                            <Text
                                color={colors.red}
                            >
                                {isReported ? "신고 취소" : "신고"}
                            </Text>
                        </MenuItem>
                        <MenuItem
                            onClick={() =>
                                showConfirm({
                                    message: isBlocking
                                        ? "차단을 해제하겠습니까?"
                                        : "이 사용자를 차단하겠습니까?",
                                    confirmLabel: "차단",
                                    onConfirm: () => toggleBlockProfileMutation.mutate({ nickname }),
                                })
                            }
                            disabled={toggleBlockProfileMutation.isPending}
                        >
                            {isBlocking ? "차단 해제" : "사용자 차단"}
                        </MenuItem>
                    </MenuList>
                </Menu>
                }
            </HStack>

            <Box
                onClick={handleGoToChatDetail}
                cursor={"pointer"}
                width={"100%"}
            >
                {!hiddenAt && imageUrls.length > 0 && (
                    <HStack
                        overflowX="auto"
                        width="100%"
                        spacing={2}
                        flexWrap="nowrap"
                    >
                        {imageUrls.map((imageUrl, index) => (
                            <Box
                                key={`${imageUrl}-${index}`}
                                width={48}
                                height={48}
                                borderRadius={12}
                                overflow="hidden"
                                flexShrink={0}

                                border={`0.5px solid ${colors.g5}`}

                                cursor={"pointer"}
                                onClick={(e) => {
                                    e.stopPropagation(); // 클릭 이벤트 전파 방지
                                    openImage(imageUrl);
                                }}

                            >
                                <Image
                                    src={imageUrl}
                                    alt={`${nickname} 게시글 이미지 ${index + 1}`}
                                    width={"100%"}
                                    height={"100%"}
                                    objectFit={"cover"}
                                />
                            </Box>
                        ))}

                        {selectedImageUrl && (
                            <ImageModal
                                isOpen={isImageModalOpen}
                                onClose={closeImage}
                                imageUrl={selectedImageUrl}
                            />
                        )}
    
                    </HStack>
                )}

                <Stack 
                    width={"100%"}
                    marginTop={!hiddenAt && imageUrls.length > 0 ? 2 : 0}
                    paddingY={2}
                >
                    {hiddenAt ? 
                        <HStack width={"100%"} justifyContent={"flex-start"} alignItems={"center"}>
                            <Icon as={ViewOffIcon} color={colors.g4} width={5} height={5} />
                            <Text fontSize={16} fontWeight={400} color={colors.g4} whiteSpace={"pre-wrap"}>
                                관리자에 의해 가려진 게시글입니다.
                            </Text>
                        </HStack>
                    :
                        <Text fontSize={16} fontWeight={400} color={colors.g1} whiteSpace={"pre-wrap"}>
                            {content}
                        </Text>
                    }
                </Stack>
            </Box>

            <Box
                onClick={handleGoToChatDetail}
                cursor={"pointer"}
                width={"100%"}
            >
                <HStack 
                    width={"100%"} 
                    justifyContent={"space-between"} 
                    alignItems={"center"}
                >
                    <HStack 
                        gap={5} 
                        justifyContent={"flex-start"}
                        alignItems={"center"}
                    >
                        <HStack
                            gap={1}
                            alignItems={"center"}
                            color={isLiked ? colors.red : colors.g2}
                        >
                            <Box
                                as={"button"}
                                type={"button"}
                                display="flex"
                                alignItems="center"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setLikePulse((prev) => prev + 1);
                                    toggleLikeChatMutation.mutate({ slug });
                                }}
                                disabled={toggleLikeChatMutation.isPending}
                            >
                                <AnimatedReactionIcon
                                    src={isLiked ? "/svg/heart-filled.svg" : "/svg/heart.svg"}
                                    alt={"좋아요"}
                                    pulseKey={likePulse}
                                />
                            </Box>
                            <Text
                                as={"button"}
                                type={"button"}
                                fontSize={15}
                                fontWeight={500}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onLikesModalOpen();
                                }}
                            >
                                {likeCount}
                            </Text>
                        </HStack>
                        <HStack
                            as={"button"}
                            type={"button"}
                            gap={1}
                            alignItems={"center"}
                            color={colors.g2}
                            onClick={handleGoToChatDetail}
                        >
                            <MyIcon src={"/svg/message-circle.svg"} alt={"댓글"} width={20} height={20} />
                            <Text fontSize={15} fontWeight={500}>
                                {commentCount}
                            </Text>
                        </HStack>
                        <HStack
                            as={"button"}
                            type={"button"}
                            gap={1}
                            alignItems={"center"}
                            color={isSaved ? colors.blue : colors.g2}
                            onClick={(e) => {
                                e.stopPropagation(); // 클릭 이벤트 전파 방지
                                setSavePulse((prev) => prev + 1);
                                toggleSaveChatMutation.mutate({ slug });
                            }}
                            disabled={toggleSaveChatMutation.isPending}
                        >
                            <AnimatedReactionIcon
                                src={isSaved ? "/svg/save-filled.svg" : "/svg/save-line.svg"}
                                alt={"저장"}
                                pulseKey={savePulse}
                            />
                            <Text fontSize={15} fontWeight={500}>
                                {saveCount}
                            </Text>
                        </HStack>
                        
                    </HStack>

                    <Text fontSize={12} fontWeight={500} color={colors.g3}>
                        {formatRelativeTime(createdAt)}
                    </Text>
                </HStack>
            </Box>

            <LikesModal
                isOpen={isLikesModalOpen}
                onClose={onLikesModalClose}
                targetType="chat"
                targetId={id}
            />

            <ConfirmAlert
                {...confirmAlertProps}
                isLoading={
                    deleteChatMutation.isPending ||
                    toggleReportChatMutation.isPending ||
                    toggleBlockProfileMutation.isPending
                }
            />
        </Stack>
    )
}

export default React.memo(ChatListItem);